"use client";

import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { XIcon } from 'lucide-react';
import { useRef, useState, useEffect } from 'react';
import { usePreviewCanvas } from '@/hooks/use-preview-canvas';

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  url?: string;
  titleClose?: string;
  // 可选：提供则显示“提取当前帧”按钮
  onExtractCurrentFrame?: (seconds: number) => void | Promise<void>;
  titleExtract?: string;
};

export const VideoPreviewDialog = ({ open, onOpenChange, url, titleClose, onExtractCurrentFrame, titleExtract }: Props) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [extracting, setExtracting] = useState(false);
  const isPreview = usePreviewCanvas();

  // 预览弹窗关闭时，显式暂停并清空 src，便于浏览器回收资源
  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      try {
        const v = videoRef.current;
        if (v) {
          v.pause();
          v.removeAttribute('src');
          v.load();
        }
      } catch {}
    }
    onOpenChange(nextOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent showCloseButton={false} className="sm:max-w-6xl p-0 bg-transparent border-0 shadow-none ring-0">
        {url ? (
          <div className="relative max-h-[85vh] max-w-[90vw] flex items-center justify-center">
            <div className="relative inline-flex items-center justify-center overflow-hidden rounded-2xl border-l-[5px] border-r-[5px] border-b-[5px] border-t-[5px] border-border bg-background/80 backdrop-blur-sm">
              <video
                ref={videoRef}
                src={url}
                controls
                autoPlay
                className="block h-auto w-auto max-h-[85vh] max-w-[90vw]"
              />
              {onExtractCurrentFrame ? (
                <Button
                  aria-label={titleExtract ?? 'Extract current frame'}
                  onClick={async () => {
                    try {
                      if (extracting) return;
                      if (isPreview) return;
                      setExtracting(true);
                      const tSec = Number(videoRef.current?.currentTime ?? NaN);
                      if (!Number.isFinite(tSec) || tSec < 0) return;
                      await onExtractCurrentFrame(tSec);
                      onOpenChange(false);
                    } finally {
                      setExtracting(false);
                    }
                  }}
                  variant="secondary"
                  size="sm"
                  className="absolute top-2 right-12 h-9 rounded-full bg-background text-foreground border shadow-md hover:bg-background/90"
                  disabled={extracting || isPreview}
                >
                  {titleExtract ?? 'Extract current frame'}
                </Button>
              ) : null}
              <Button
                aria-label={titleClose ?? 'Close preview'}
                onClick={() => onOpenChange(false)}
                variant="ghost"
                size="icon"
                className="absolute top-2 right-2 rounded-full bg-background text-foreground border shadow-md hover:bg-background/90"
              >
                <XIcon size={16} />
              </Button>
            </div>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};


